import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";
import { VizConfig } from "@/shared/config";
import { messagePathRepresentationMappings } from "@/shared/domain/topics";
import { isAbortError } from "@/shared/errors";
import { ErrorMonitoringService } from "@/shared/services";
import { State } from "@/shared/state/visualization";
import { useDatasources } from "@/shared/state/visualization/hooks";

import { Timer } from "../timer";

import { EphemeralWorkspaceState } from "./EphemeralWorkspaceState";
import { DEFAULT_TIME_DISPLAY_MODE, TimeDisplayMode } from "./TimeDisplayMode";
import { useLayoutState } from "./useLayoutState";
import { WorkspaceData, WorkspaceDataService } from "./WorkspaceDataService";

export const useWorkspaceState = (): EphemeralWorkspaceState => {
  const domainServices = useDomainServices();
  const queryClient = useQueryClient();
  const datasources = useDatasources();
  // Scopes the session-file listings the fetcher issues to the current org.
  const orgId = useCurrentOrgId();

  const workspaceDataService = React.useMemo(
    () => new WorkspaceDataService(domainServices, queryClient, orgId),
    [domainServices, queryClient, orgId],
  );

  // Layout workspace state
  const layoutState = useLayoutState();

  // Domain data state
  const [workspaceData, setWorkspaceData] = React.useState<WorkspaceData>({
    files: [],
    sessions: [],
    topics: [],
    messagePaths: [],
  });

  // Data loading and error state
  const [isDataLoading, setIsDataLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);

  // Workspace UI processing state
  const [isHydrating, setIsHydrating] = React.useState(true);

  // Panel selection state
  const [selectedPanelId, setSelectedPanelId] = React.useState<string | null>(
    null,
  );

  // Workspace snapshot for tracking changes
  const workspaceSnapshotRef = React.useRef({
    panels: VizConfig.default().panels,
    layout: VizConfig.default().layout,
  });

  // Panel data errors
  const [dataMatchingErrors, setDataMatchingErrors] = React.useState<
    EphemeralWorkspaceState["dataMatchingErrors"]
  >({});

  // Time display mode (Elapsed/Log/Local)
  const [timeDisplayMode, setTimeDisplayMode] = React.useState<TimeDisplayMode>(
    DEFAULT_TIME_DISPLAY_MODE,
  );

  const timerRef = React.useRef(new Timer({ spans: [] }));

  React.useEffect(
    function initializeTimer() {
      // Immediately reset the timer when the time spans change.
      timerRef.current.reset();

      const fileToTimeBounds = workspaceData.topics.reduce(
        (accum, topic) => {
          if (
            topic.start_time === null ||
            topic.start_time === undefined ||
            topic.end_time === null ||
            topic.end_time === undefined
          ) {
            return accum;
          }

          if (!(topic.association.association_id in accum)) {
            accum[topic.association.association_id] = [
              topic.start_time,
              topic.end_time,
            ];
          }

          const currAccum = accum[topic.association.association_id];

          if (currAccum[0] > topic.start_time) {
            currAccum[0] = topic.start_time;
          }

          if (currAccum[1] < topic.end_time) {
            currAccum[1] = topic.end_time;
          }

          return accum;
        },
        {} as { [key: string]: [bigint, bigint] },
      );
      const spans: [bigint, bigint][] = Object.values(fileToTimeBounds);
      const mergedAndSorted = Timer.sortAndMergeSpans(spans);
      timerRef.current.updateTimeSpans(mergedAndSorted);
    },
    [workspaceData.topics],
  );

  React.useEffect(
    function loadWorkspaceData() {
      if (datasources.length === 0) {
        setIsDataLoading(false);
        return;
      }

      setIsDataLoading(true);

      const abortController = new AbortController();

      workspaceDataService
        .fetchWorkspaceData(datasources, abortController)
        .then((workspaceData) => {
          setError(null);
          setWorkspaceData(workspaceData);
        })
        .catch((err: Error) => {
          if (abortController.signal.aborted || isAbortError(err)) {
            return;
          }
          // The user sees a workspace-level error, which says nothing about
          // which datasource failed to resolve; report it so a failed load is
          // diagnosable after the fact.
          ErrorMonitoringService.captureError(err);
          setError(err);
        })
        .finally(() => {
          if (!abortController.signal.aborted) {
            setIsDataLoading(false);
          }
        });

      return () => abortController.abort();
    },
    [datasources, workspaceDataService],
  );

  const panelHandlers = React.useMemo(
    () => ({
      selectPanel: (panelId: string) => setSelectedPanelId(panelId),
      clearPanelSelection: () => setSelectedPanelId(null),
      clearPanelSelectionIfMatches: (panelId: string) => {
        setSelectedPanelId((current) => (current === panelId ? null : current));
      },
    }),
    [],
  );

  const workspaceHandlers = React.useMemo(
    () => ({
      setWorkspaceSnapshot: (snapshot: {
        panels: State["panels"];
        layout: State["layout"];
      }) => {
        workspaceSnapshotRef.current = snapshot;
      },
    }),
    [],
  );

  // Ephemeral panel state (viewport, filters, etc.)
  const ephemeralPanelStateRef = React.useRef(
    new Map<string, Record<string, unknown>>(),
  );

  const updateEphemeralPanelState = React.useCallback(
    (panelId: string, updates: Record<string, unknown>) => {
      const existing = ephemeralPanelStateRef.current.get(panelId) ?? {};
      ephemeralPanelStateRef.current.set(panelId, { ...existing, ...updates });
    },
    [],
  );

  // Each topic's representation mappings, computed once from topics so media
  // panels can open sources without a per-topic representations request.
  const mappingsByTopicId = React.useMemo(
    () =>
      new Map(
        workspaceData.topics.map((topic) => [
          topic.topic_id,
          messagePathRepresentationMappings(topic),
        ]),
      ),
    [workspaceData.topics],
  );

  return React.useMemo(
    (): EphemeralWorkspaceState => ({
      // Server state (files, sessions, topics, messagePaths from the fetcher)
      ...workspaceData,
      mappingsByTopicId,
      isDataLoading: layoutState.isLoading || isDataLoading,
      error,
      layoutError: layoutState.error,

      // Panel selection
      selectedPanelId,
      ...panelHandlers,

      // Timer
      timer: timerRef.current,

      // Layout UI state
      activeLayout: layoutState.activeLayout,
      setActiveLayout: layoutState.setActiveLayout,
      skipLayoutRehydration: layoutState.skipLayoutRehydration,
      isHydrating,
      setIsHydrating,
      workspaceSnapshot: workspaceSnapshotRef.current,
      ...workspaceHandlers,

      // Panel data errors
      dataMatchingErrors,
      setDataMatchingErrors,

      // Time display mode
      timeDisplayMode,
      setTimeDisplayMode,

      // Ephemeral panel state
      ephemeralPanelState: ephemeralPanelStateRef.current,
      updateEphemeralPanelState,
    }),
    [
      layoutState.activeLayout,
      layoutState.isLoading,
      layoutState.error,
      layoutState.setActiveLayout,
      layoutState.skipLayoutRehydration,
      workspaceData,
      mappingsByTopicId,
      isDataLoading,
      error,
      selectedPanelId,
      panelHandlers,
      workspaceHandlers,
      isHydrating,
      dataMatchingErrors,
      timeDisplayMode,
      updateEphemeralPanelState,
    ],
  );
};
